import React, { useState, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { Loader2, Upload, MapPin, Fuel, Mountain, Sparkles, X, Store } from 'lucide-react';
import { formatApiErrorDetail } from '@/lib/errors';
import { useI18n } from '@/i18n';

const CATEGORIES = [
  { key: 'viewpoint', icon: Mountain, color: '#9DEDFF', label: 'addspot.cat.viewpoint', sub: 'addspot.cat.viewpoint_sub' },
  { key: 'parking', icon: MapPin, color: '#FFD479', label: 'addspot.cat.parking', sub: 'addspot.cat.parking_sub' },
  { key: 'gas_station', icon: Fuel, color: '#10B981', label: 'addspot.cat.gas', sub: 'addspot.cat.gas_sub' },
  { key: 's365', icon: Store, color: '#FF7A00', label: 'addspot.cat.s365', sub: 'addspot.cat.s365_sub' },
  { key: 'spot', icon: Sparkles, color: '#A78BFA', label: 'addspot.cat.spot', sub: 'addspot.cat.spot_sub' },
];

export default function AddSpotDialog({ open, onOpenChange, gameId, coords, onCreated }) {
  const { t } = useI18n();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('spot');
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const fileRef = useRef(null);

  const reset = () => {
    setTitle(''); setDescription(''); setCategory('spot');
    setFile(null); setPreview(null);
  };

  const handleFile = (f) => {
    if (!f) return;
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!coords) return;
    setLoading(true);
    try {
      let image_path = null;
      if (file) {
        const fd = new FormData();
        fd.append('file', file);
        const { data: up } = await api.post('/upload', fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        image_path = up.path;
      }
      const { data } = await api.post('/spots', {
        game_id: gameId,
        title,
        description,
        category,
        x: coords.x,
        y: coords.y,
        image_path,
      });
      toast.success(t('addspot.created'));
      onCreated?.(data);
      reset();
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || 'Erreur');
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) reset(); onOpenChange(o); }}>
      <DialogContent className="glass-strong border-white/10 text-white max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl uppercase tracking-tighter">
            {t('addspot.title')}
          </DialogTitle>
          <DialogDescription className="text-white/60">
            {t('addspot.desc')}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          {/* Category grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {CATEGORIES.map((c) => {
              const Icon = c.icon;
              const active = category === c.key;
              return (
                <button
                  key={c.key}
                  type="button"
                  data-testid={`addspot-cat-${c.key}`}
                  onClick={() => setCategory(c.key)}
                  className={`p-3 rounded-xl border-2 transition-all text-left ${
                    active
                      ? 'bg-white/5'
                      : 'border-white/10 bg-white/5 hover:border-white/30'
                  }`}
                  style={active ? { borderColor: c.color, background: `${c.color}11` } : {}}
                >
                  <Icon className="w-5 h-5 mb-1" style={{ color: c.color }} />
                  <div className="font-display font-bold uppercase text-xs">{t(c.label)}</div>
                  <div className="text-[10px] text-white/50 leading-tight mt-0.5">{t(c.sub)}</div>
                </button>
              );
            })}
          </div>

          <div>
            <Label htmlFor="title" className="text-white/80">{t('addspot.field.title')}</Label>
            <Input
              id="title"
              required
              data-testid="addspot-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={t('addspot.placeholder.title')}
              className="bg-black/40 border-white/10 text-white placeholder:text-white/30"
            />
          </div>
          <div>
            <Label htmlFor="description" className="text-white/80">{t('addspot.field.desc')}</Label>
            <Textarea
              id="description"
              rows={3}
              data-testid="addspot-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t('addspot.placeholder.desc')}
              className="bg-black/40 border-white/10 text-white placeholder:text-white/30 resize-none"
            />
          </div>

          <div>
            <Label className="text-white/80">{t('addspot.upload.title')}</Label>
            {preview ? (
              <div className="relative mt-1 rounded-xl overflow-hidden border border-white/10">
                <img src={preview} alt="preview" className="w-full max-h-56 object-cover" />
                <button
                  type="button"
                  onClick={() => { setFile(null); setPreview(null); }}
                  className="absolute top-2 right-2 bg-black/70 rounded-full p-1.5 hover:bg-black"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <button
                type="button"
                data-testid="addspot-upload-btn"
                onClick={() => fileRef.current?.click()}
                className="mt-1 w-full border-2 border-dashed border-white/15 rounded-xl p-6 hover:border-[#00F0FF] hover:bg-white/5 transition-all text-center"
              >
                <Upload className="w-5 h-5 mx-auto text-white/60" />
                <div className="text-sm text-white/60 mt-2">{t('addspot.upload.cta')}</div>
                <div className="text-xs text-white/40">{t('addspot.upload.hint')}</div>
              </button>
            )}
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => handleFile(e.target.files?.[0])}
            />
          </div>

          <Button
            type="submit"
            disabled={loading || !title}
            data-testid="addspot-submit"
            className="w-full bg-[#FF007F] hover:bg-[#FF007F]/90 text-white font-bold uppercase tracking-wide rounded-full"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : t('addspot.submit')}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
